import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Boxes, Minus, Plus, Sparkles } from "lucide-react";

export default function BulkBox() {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({}); // slug -> count
  const [loading, setLoading] = useState(true);
  const { addBulkBox, openCart } = useCart();
  const { bulk_box_price, allowed_cities } = useSettings();
  const BULK_BOX_PRICE = bulk_box_price || 999;
  const zonesText = (allowed_cities || ["Delhi NCR"]).join(" / ");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, []);

  const totalJars = useMemo(
    () => Object.values(counts).reduce((s, n) => s + n, 0),
    [counts]
  );
  const remaining = 6 - totalJars;
  const canAdd = totalJars === 6;
  const MAX_PER = 2;

  const inc = (slug) => {
    const cur = counts[slug] || 0;
    if (cur >= MAX_PER) {
      toast.info(`Max ${MAX_PER} jars of the same pickle`);
      return;
    }
    if (remaining <= 0) {
      toast.info("You've picked 6 jars — that's a full family pack");
      return;
    }
    setCounts((prev) => ({ ...prev, [slug]: (prev[slug] || 0) + 1 }));
  };
  const dec = (slug) => {
    setCounts((prev) => {
      const cur = prev[slug] || 0;
      if (cur <= 0) return prev;
      const next = { ...prev, [slug]: cur - 1 };
      if (next[slug] === 0) delete next[slug];
      return next;
    });
  };

  // Individual jars 250g × 6 average price around ~185/jar = ~1110. Our pack is 999. So user saves ~₹100+.
  const individualPrice = useMemo(() => {
    let sum = 0;
    Object.entries(counts).forEach(([slug, c]) => {
      const p = products.find((pp) => pp.slug === slug);
      if (p?.prices?.["250g"]) sum += p.prices["250g"] * c;
    });
    return sum;
  }, [counts, products]);

  const savings = Math.max(0, individualPrice - BULK_BOX_PRICE);

  const addToCart = () => {
    if (!canAdd) {
      toast.error(`Pick ${remaining} more jar${remaining > 1 ? "s" : ""}`);
      return;
    }
    const selectedSlugs = [];
    const selectedNames = [];
    Object.entries(counts).forEach(([slug, c]) => {
      for (let i = 0; i < c; i++) {
        selectedSlugs.push(slug);
        const p = products.find((pp) => pp.slug === slug);
        selectedNames.push(p?.name || slug);
      }
    });
    addBulkBox({ selectedSlugs, selectedNames, price: BULK_BOX_PRICE });
    toast.success("Family Pack added to cart");
    openCart();
    setCounts({});
  };

  return (
    <div className="fade-up">
      <section className="bg-gradient-to-b from-[#F4EFE6]/60 to-[#FBF8F3] py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#5F7355]/10 text-[#5F7355] text-xs font-semibold uppercase tracking-[0.2em] mb-5">
            <Boxes className="w-3.5 h-3.5" /> Family Pack Builder
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E1B18]">Six jars. One warm box.</h1>
          <p className="mt-4 text-[#6E675F] max-w-2xl mx-auto">
            Load up on your favourites — pick any 6 pickles (250g each), up to 2 jars of the same pickle, and we&apos;ll pack them together at a family-sized price.
          </p>
          <div className="mt-6 inline-flex items-center gap-6 text-sm">
            <div><span className="font-serif text-2xl font-bold text-[#5F7355]">{formatINR(BULK_BOX_PRICE)}</span> <span className="text-[#6E675F]">/ pack of 6</span></div>
            <div className="text-[#9E3B21] font-semibold flex items-center gap-1"><Sparkles className="w-4 h-4" /> Save up to ₹150</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-semibold">Choose 6 jars</h2>
              <div className="text-sm font-medium">
                <span className="text-[#5F7355]" data-testid="bulk-selected-count">{totalJars}</span>
                <span className="text-[#6E675F]"> / 6 picked</span>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => <div key={i} className="h-32 bg-white rounded-2xl animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {products.map((p) => {
                  const c = counts[p.slug] || 0;
                  return (
                    <div
                      key={p.slug}
                      data-testid={`bulk-row-${p.slug}`}
                      className={`bg-white rounded-2xl border p-4 flex items-center gap-4 transition-all ${
                        c > 0 ? "border-[#5F7355] shadow-sm" : "border-[#E6DEC3]"
                      }`}
                    >
                      <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif font-semibold text-[#1E1B18] leading-tight">{p.name}</p>
                        <p className="text-xs text-[#6E675F] mt-0.5">{formatINR(p.prices["250g"])} / 250g</p>
                      </div>
                      <div className="flex items-center border border-[#E6DEC3] rounded-full">
                        <button
                          data-testid={`bulk-dec-${p.slug}`}
                          onClick={() => dec(p.slug)}
                          disabled={c === 0}
                          className="p-2 hover:bg-[#F4EFE6] rounded-l-full disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold" data-testid={`bulk-count-${p.slug}`}>{c}</span>
                        <button
                          data-testid={`bulk-inc-${p.slug}`}
                          onClick={() => inc(p.slug)}
                          disabled={remaining <= 0 || (counts[p.slug] || 0) >= MAX_PER}
                          className="p-2 hover:bg-[#F4EFE6] rounded-r-full disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6 sticky top-24">
              <h3 className="font-serif text-xl font-semibold mb-4">Your Family Pack</h3>
              {totalJars === 0 ? (
                <p className="text-sm text-[#6E675F]">Start picking jars from the left.</p>
              ) : (
                <ul className="space-y-2 mb-4" data-testid="bulk-selected-list">
                  {Object.entries(counts).map(([slug, c]) => {
                    const p = products.find((pp) => pp.slug === slug);
                    if (!p) return null;
                    return (
                      <li key={slug} className="flex justify-between text-sm">
                        <span>{p.name}</span>
                        <span className="text-xs text-[#6E675F]">×{c}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {savings > 0 && (
                <p className="text-xs text-[#5F7355] mb-2">You save {formatINR(savings)} vs buying individually</p>
              )}
              <div className="border-t border-[#E6DEC3] pt-4 flex justify-between font-serif text-xl font-semibold">
                <span>Total</span>
                <span>{formatINR(BULK_BOX_PRICE)}</span>
              </div>
              <Button
                data-testid="bulk-add-to-cart"
                disabled={!canAdd}
                onClick={addToCart}
                className="w-full btn-primary rounded-full mt-6 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Boxes className="w-4 h-4 mr-2" />
                {canAdd ? "Add Family Pack to Cart" : `Pick ${remaining} more jar${remaining > 1 ? "s" : ""}`}
              </Button>
              <p className="text-xs text-center text-[#6E675F] mt-3">Free delivery in {zonesText}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
